/**
 * Sign-up and Verify: how someone without a key comes to have one.
 *
 *   #/signup               email, workspace name, and the Turnstile challenge
 *   #/verify?token=sbv_…   the mailed link: make the workspace, show its key once
 *
 * Cloudflare's Turnstile script is loaded only here, when the Sign-up screen
 * is opened. Leaving the screen after it loaded reloads the page, so the
 * script never lives on beside a key: every other screen runs nothing but
 * the console's own script.
 */

import { Api, type SignedUp } from './api';
import { Notices, field, h, mount, nameBox } from './dom';
import { workspaceName } from './names';
import { defaultBase, saveSession } from './session';

interface TurnstileOptions {
  sitekey: string;
  action?: string;
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
}
interface Turnstile {
  render(el: HTMLElement, options: TurnstileOptions): string;
  reset(id: string): void;
  remove(id: string): void;
}
declare global {
  interface Window { turnstile?: Turnstile }
}

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let loading: Promise<Turnstile> | null = null;

const loadTurnstile = (): Promise<Turnstile> => {
  loading ??= new Promise<Turnstile>((resolve, reject) => {
    if (window.turnstile) return resolve(window.turnstile);
    const script = h('script', { src: SCRIPT });
    script.addEventListener('load', () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile did not start'))));
    script.addEventListener('error', () => {
      loading = null;
      script.remove();
      reject(new Error('Turnstile could not be loaded'));
    });
    document.head.append(script);
  });
  return loading;
};

/** A problem of the console's own, shown the way the API's are. */
const trouble = (status: number, title: string, detail: string) => ({ type: 'about:blank', title, status, detail });

/** The Sign-up screen. Returns what to undo when the screen is left: the widget. */
export function renderSignup(view: HTMLElement): () => void {
  const notices = new Notices();
  const base = h('input', { type: 'url', required: true, value: defaultBase(), placeholder: 'https://…', autocomplete: 'url' });
  const email = h('input', { type: 'email', required: true, placeholder: 'you@example.com', autocomplete: 'email' });
  const workspace = h('input', {
    type: 'text', required: true, placeholder: 'acme', autocomplete: 'off', spellcheck: 'false', autocapitalize: 'none',
  });
  const workspaceBox = nameBox(workspace, workspaceName);
  const challenge = h('div', { class: 'turnstile' }, h('p', { class: 'hint' }, 'Loading the challenge…'));
  const submit = h('button', { class: 'primary', type: 'submit', disabled: true }, 'Sign up');
  const form = h('form', { class: 'card' });

  let widget: { t: Turnstile; id: string } | null = null;
  let token: string | null = null;
  let left = false;

  const forgetWidget = () => {
    if (widget) widget.t.remove(widget.id);
    widget = null;
    token = null;
    submit.disabled = true;
  };

  /** Ask this base URL for its sitekey and put the challenge up; the sitekey belongs to the API, so a new base means a new widget. */
  const prepare = async () => {
    forgetWidget();
    notices.clear();
    mount(challenge, h('p', { class: 'hint' }, 'Loading the challenge…'));
    const config = await new Api(base.value, '').signupConfig();
    if (left) return;
    if (!config.ok) {
      mount(challenge);
      return notices.show(config.problem);
    }
    if (!config.value.available || !config.value.turnstileSitekey) {
      mount(challenge);
      return notices.show(trouble(503, 'Sign-up Not Configured', 'Sign-up is not configured on this API yet. Ask its operator for a key instead.'));
    }
    let t: Turnstile;
    try {
      t = await loadTurnstile();
    } catch (error) {
      mount(challenge);
      return notices.show(trouble(0, 'Unreachable', `${String(error)}; check your connection or content blocker, then reload`));
    }
    if (left) return;
    mount(challenge);
    widget = {
      t,
      id: t.render(challenge, {
        sitekey: config.value.turnstileSitekey,
        action: 'signup',
        callback: (solved) => { token = solved; submit.disabled = false; },
        'expired-callback': () => { token = null; submit.disabled = true; },
        'error-callback': () => { token = null; submit.disabled = true; },
      }),
    };
  };

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!token) return;
    const name = workspaceBox.read();
    if (!name) return workspace.focus();
    submit.disabled = true;
    const answer = await new Api(base.value, '').signup({ email: email.value.trim(), workspace: name, turnstileToken: token });
    // A challenge's token is good once, whatever the answer was.
    token = null;
    if (widget) widget.t.reset(widget.id);
    if (!answer.ok) return notices.show(answer.problem);
    forgetWidget();
    mount(view, h('section', { class: 'narrow' },
      h('h2', {}, 'Check your mail'),
      h('div', { class: 'card' },
        h('p', {}, answer.value.detail),
        h('p', { class: 'hint' }, `The link makes the workspace "${name}" and shows its first key, once. Open it in this browser or any other.`))));
  });

  mount(form,
    field('API base URL', base, 'the API to make the workspace on'),
    field('Email', email, 'where the verification link goes; it is kept as the workspace owner\'s'),
    field('Workspace', workspace, 'any name; it is saved in lower case, with dashes for spaces, and names your Worlds to others', workspaceBox.hint),
    challenge,
    submit);
  mount(view,
    h('section', { class: 'narrow' },
      h('h2', {}, 'Sign up'),
      h('p', { class: 'lede' }, 'Make a workspace of your own. We mail you a link; opening it makes the workspace and shows its first API key.'),
      form,
      notices.el,
      h('p', { class: 'hint' }, 'Have a key already? ', h('a', { href: '#/connect' }, 'Connect'))));
  base.addEventListener('change', () => void prepare());
  void prepare();
  email.focus();
  return () => {
    left = true;
    forgetWidget();
    // The hash already names the next screen; a reload opens it without Turnstile's script.
    if (loading) location.reload();
  };
}

/** The Verify screen, opened from the mailed link. `onConnect` takes the new key into the console. */
export function renderVerify(view: HTMLElement, token: string | null, onConnect: (api: Api) => void): void {
  const notices = new Notices();
  if (!token) {
    mount(view, h('section', { class: 'narrow' },
      h('h2', {}, 'Verify'),
      h('p', { class: 'lede' }, 'This page is opened from the link in a sign-up mail, and that link carries a token. This one has none.'),
      h('p', {}, h('a', { href: '#/signup' }, 'Sign up'), ' · ', h('a', { href: '#/connect' }, 'Connect'))));
    return;
  }
  const base = h('input', { type: 'url', required: true, value: defaultBase(), placeholder: 'https://…', autocomplete: 'url' });
  const go = h('button', { class: 'primary', type: 'submit' }, 'Make my workspace');

  // Only on a click: a mail scanner that opens the link runs no click, so it spends nothing.
  const verify = async (e: Event) => {
    e.preventDefault();
    go.disabled = true;
    const answer = await new Api(base.value, '').verifySignup(token);
    if (!answer.ok) {
      // Unreachable: the token is untouched, so keep it for another try, perhaps at another base URL.
      go.disabled = answer.problem.status !== 0;
      if (answer.problem.status !== 0) history.replaceState(null, '', '#/verify');
      notices.show(answer.problem);
      return;
    }
    // Spent: keep it out of the address bar and history.
    history.replaceState(null, '', '#/verify');
    mount(view, created(answer.value, () => onConnect(saveSession(base.value, answer.value.key.secret))));
  };

  mount(view,
    h('section', { class: 'narrow' },
      h('h2', {}, 'Verify'),
      h('p', { class: 'lede' }, 'One step left: make the workspace and see its first key. The link works once.'),
      h('form', { class: 'card', onsubmit: verify }, field('API base URL', base, 'the API you signed up on'), go),
      notices.el));
}

const created = (made: SignedUp, connect: () => void): HTMLElement => {
  const copy = h('button', {
    class: 'small', type: 'button',
    onclick: async () => {
      try {
        await navigator.clipboard.writeText(made.key.secret);
        copy.textContent = 'Copied';
      } catch {
        copy.textContent = 'Select and copy it by hand';
      }
    },
  }, 'Copy');
  return h('section', { class: 'narrow' },
    h('h2', {}, `Workspace ${made.workspace}`),
    h('p', { class: 'lede' }, `Made on the ${made.plan} plan, owned by ${made.ownerEmail}.`),
    h('div', { class: 'notice secret' },
      h('div', { class: 'notice-head' }, h('strong', {}, `Owner key ${made.key.id} (${made.key.scopes.join(', ')})`), copy),
      h('p', { class: 'detail' }, 'Shown once. Store it now: the API keeps only its hash. With it you can make narrower keys under /v1/keys.'),
      h('code', { class: 'secret-value' }, made.key.secret)),
    h('p', {}, h('button', { class: 'primary', type: 'button', onclick: connect }, 'Connect with this key')));
};
